import { CropLabel } from "./CropLabel";
import { Popup } from "./Popup";
import { classes } from "./react";

export type CropCircleProps = {
    /**
     * Additional `className` for the component.
     */
    className?: string;
    /**
     * Event handler fired when the component is clicked.
     */
    onClick?: React.MouseEventHandler<HTMLSpanElement>;
    /**
     * The background color of the crop circle.
     */
    color: React.CSSProperties["backgroundColor"];
    /**
     * The name of the crop, to display as a tooltip.
     */
    title?: string;
};

/**
 * Makes a guess at whether the CSS color is too light to see clearly on a white background.
 * @param color The CSS color to check.
 */
function cssColorRequiresBorder(color: string | null | undefined) {
    const colorToLower = color?.toLowerCase();
    return !colorToLower || ["fff", "ffffff", "transparent"].includes(colorToLower);
}

/**
 * A tiny coloured circle, for a crop.
 */
export const CropCircle = ({ className, color, onClick, title }: CropCircleProps) => {
    return (
        <span
            className={classes(
                "crop-circle",
                {
                    bordered: cssColorRequiresBorder(color),
                },
                className
            )}
            style={{ backgroundColor: color }}
            onClick={onClick}
            title={title}
        />
    );
};

export type CropCirclesProps = {
    /**
     * Additional `className` for the component.
     */
    className?: string;
    /**
     * The list of crops to display.
     */
    items: CropCircleProps[];
    /**
     * Whether to display a popover list of all crop names on click.
     * @default true
     */
    popover?: boolean;
};

export function CropCircles({ className, items, popover = true }: CropCirclesProps) {
    const cropCircles = (
        <ul className={classes("crop-circles", className)}>
            {items.map(({ className, ...props }, i) => (
                <li key={i} className="crop-circles--item">
                    <CropCircle className={classes("crop-circles--crop-circle", className)} {...props} />
                </li>
            ))}
        </ul>
    );

    if (!popover) {
        return cropCircles;
    }

    return (
        <Popup
            popupCssClass="crop-circles--popover"
            popupContent={() => (
                <ul className="crop-circles--popover-list">
                    {items.map((props, i) => (
                        <li key={i}>
                            <CropLabel {...props} />
                        </li>
                    ))}
                </ul>
            )}
        >
            {cropCircles}
        </Popup>
    );
}
